import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import ScrollToTop from './ScroollToTop';

// Editable content configuration
const aboutContent = {
  hero: {
    title: "About RipeWood",
    subtitle: "Crafting Timeless Furniture Since 1995",
    description: "We believe that furniture is more than just functional pieces—it's the foundation of memories, comfort, and style in every space.",
    backgroundImage: "https://images.pexels.com/photos/1457842/pexels-photo-1457842.jpeg?auto=compress&cs=tinysrgb&w=1920"
  },

  story: {
    title: "Our Story",
    content: [
      "Founded in 1995, RipeWood began as a small workshop with a simple vision: to create furniture that tells a story. What started as a passion project by master craftsman John Ripewood has grown into a trusted name in premium furniture design.",
      "Today, we combine traditional craftsmanship with modern design sensibilities to create pieces that stand the test of time. Each item in our collection is thoughtfully designed and meticulously crafted to bring beauty and functionality to your space.",
      "Our commitment to sustainability and quality has remained unchanged since day one. We source materials responsibly and work with skilled artisans who share our dedication to excellence."
    ],
    image: "/assets/Gallery3.png"
  },

  mission: {
    title: "Our Mission",
    statement: "To shape timber into living narratives—creating furniture that becomes part of your story, your rituals, and your soul.",
    values: [
      {
        icon: "🌲",
        title: "Sustainability",
        description: "We use responsibly sourced materials and eco-friendly practices in every step of our process."
      },
      {
        icon: "🪚",
        title: "Quality Craftsmanship",
        description: "Every piece is handcrafted by skilled artisans with decades of experience and attention to detail."
      },
      {
        icon: "🪑",
        title: "Timeless Design",
        description: "We create furniture that transcends trends, offering classic beauty that lasts for generations."
      },
      {
        icon: "🏠",
        title: "Customer First",
        description: "Your satisfaction is our priority. We're committed to providing exceptional service and support."
      }
    ]
  },

  team: {
    title: "Meet Our Team",
    description: "The passionate people behind RipeWood",
    members: [
      {
        name: "John Ripewood",
        role: "Founder & Master Craftsman",
        image: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=400",
        bio: "With over 30 years of experience, John leads our design and craftsmanship vision."
      },
      {
        name: "Sarah Mitchell",
        role: "Design Director",
        image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=400",
        bio: "Sarah brings contemporary aesthetics to our traditional craftsmanship approach."
      },
      {
        name: "Michael Chen",
        role: "Operations Manager",
        image: "https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=400",
        bio: "Michael ensures every piece meets our exacting standards of quality."
      },
      {
        name: "Emma Rodriguez",
        role: "Customer Relations",
        image: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=400",
        bio: "Emma is dedicated to making your furniture buying experience exceptional."
      }
    ]
  },

  stats: {
    title: "RipeWood by the Numbers",
    backgroundImage: "/assets/premium_hero_3.png",
    figures: [
      { number: 28, suffix: "+", label: "Years in Business" },
      { number: 10000, suffix: "+", label: "Happy Customers" },
      { number: 50, suffix: "+", label: "Skilled Artisans" },
      { number: 100, suffix: "%", label: "Sustainable Materials" }
    ]
  },

  cta: {
    title: "Ready to Transform Your Space?",
    description: "Explore our collections and discover furniture that tells your story.",
    buttonText: "View Collections",
    buttonLink: "/livingroom"
  }
};

// Counter Animation Component
const AnimatedCounter = ({ target, suffix, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      let startTime;
      let animationFrame;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = (currentTime - startTime) / duration;

        if (progress < 1) {
          setCount(Math.floor(target * progress));
          animationFrame = requestAnimationFrame(animate);
        } else {
          setCount(target);
        }
      };

      animationFrame = requestAnimationFrame(animate);

      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }
  }, [inView, target, duration]);

  const formatNumber = (num) => {
    if (num >= 1000) {
      return num.toLocaleString();
    }
    return num;
  };

  return (
    <div ref={ref} className="text-3xl sm:text-4xl md:text-5xl font-bold text-yellow-500 mb-2">
      {formatNumber(count)}{suffix}
    </div>
  );
};

const AboutUs = () => {
  return (
    <div className="bg-white">
      <ScrollToTop />

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center h-[50vh] sm:h-[60vh] md:h-[70vh] flex items-center"
        style={{ backgroundImage: `url('${aboutContent.hero.backgroundImage}')` }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">{aboutContent.hero.title}</h1>
          <p className="text-lg sm:text-xl md:text-2xl mb-3 sm:mb-4 text-gray-200">{aboutContent.hero.subtitle}</p>
          <p className="text-base sm:text-lg max-w-3xl mx-auto px-4">{aboutContent.hero.description}</p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 text-gray-900">{aboutContent.story.title}</h2>
              {aboutContent.story.content.map((paragraph, index) => (
                <p key={index} className="text-gray-700 mb-3 sm:mb-4 leading-relaxed text-base sm:text-lg">
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative overflow-hidden bg-gray-100 rounded-lg shadow-xl">
                <div className="aspect-[4/3] w-full">
                  <img
                    src={aboutContent.story.image}
                    alt="Our Story"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://placehold.co/800x600/E5E7EB/6B7280?text=Our+Story";
                    }}
                    style={{ objectPosition: 'center 20%' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-48 h-48 sm:w-72 sm:h-72 bg-yellow-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 right-0 w-48 h-48 sm:w-72 sm:h-72 bg-gray-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-gray-900 tracking-tight">{aboutContent.mission.title}</h2>
            <div className="max-w-3xl mx-auto px-4">
              <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed font-light italic border-l-4 border-yellow-500 pl-3 sm:pl-4 py-2 sm:py-3 bg-white/50 backdrop-blur-sm rounded-r-lg shadow-sm">
                "{aboutContent.mission.statement}"
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-8 sm:mt-12">
            {aboutContent.mission.values.map((value, index) => (
              <div
                key={index}
                className="group relative bg-white p-5 sm:p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 overflow-hidden"
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                {/* Content */}
                <div className="relative z-10 text-center">
                  <div className="text-3xl sm:text-4xl mb-3 sm:mb-4 transform group-hover:scale-110 transition-transform duration-300 inline-block">
                    {value.icon}
                  </div>
                  <h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3 text-gray-900 group-hover:text-yellow-600 transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {value.description}
                  </p>
                </div>

                {/* Decorative corner accent */}
                <div className="absolute top-0 right-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-yellow-400 to-yellow-600 opacity-0 group-hover:opacity-10 transition-opacity duration-300 rounded-bl-full"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section
        className="relative py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gray-900"
        style={{
          backgroundImage: `url('${aboutContent.stats.backgroundImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Dark overlay for better text visibility */}
        <div className="absolute inset-0 bg-black/70"></div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-white">{aboutContent.stats.title}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {aboutContent.stats.figures.map((stat, index) => (
              <div key={index} className="text-center">
                <AnimatedCounter
                  target={stat.number}
                  suffix={stat.suffix}
                  duration={2000}
                />
                <div className="text-sm sm:text-base md:text-lg text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 text-gray-900">{aboutContent.team.title}</h2>
            <p className="text-lg sm:text-xl text-gray-600">{aboutContent.team.description}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {aboutContent.team.members.map((member, index) => (
              <div key={index} className="text-center">
                <div className="relative overflow-hidden bg-gray-100 rounded-full mx-auto mb-4 w-40 h-40 sm:w-48 sm:h-48">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover shadow-lg"
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://placehold.co/400x400/E5E7EB/6B7280?text=${encodeURIComponent(member.name)}`;
                    }}
                  />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">{member.name}</h3>
                <p className="text-yellow-600 font-medium mb-2 text-sm sm:text-base">{member.role}</p>
                <p className="text-gray-600 text-sm px-2">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-gray-900 to-gray-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">{aboutContent.cta.title}</h2>
          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 text-gray-200 px-4">{aboutContent.cta.description}</p>
          <Link
            to={aboutContent.cta.buttonLink}
            className="inline-block bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold py-3 px-6 sm:py-4 sm:px-8 text-sm sm:text-base rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg no-underline"
          >
            {aboutContent.cta.buttonText}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
